// src/components/products/ProductForm/GeneralInfoSections/PricingOperationsSection.jsx

import React, { useMemo } from "react";
import { Grid } from "@mui/material";
import FormInput from "../../../inputs/FormInput";
import useGetCitiesForSelect from "../../../../hooks/cities/useGetCitiesForSelect";
import { transformOptionsToIdName } from "../../../../utils/forms/transformHelper";
import { PRICING_UNIT_MAP } from "../../../../utils/programs/pricingConstants";
// import DateInput from "../../../inputs/DateInput";

const PricingOperationsSection = ({
    formData,
    setFormData,
    errors,
    isArabic,
    activeLang,
    handleMultiLangChange,
    handlePricingUnitChange,
    pricingUnitsOptions,
    loadingPricingUnits,
}) => {
    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    const { options: cities, isLoading: loadingCities } = useGetCitiesForSelect();
    const cityOptions = useMemo(() => transformOptionsToIdName(cities), [cities]);

    const isPerPerson = formData.pricing_unit_id && PRICING_UNIT_MAP[formData.pricing_unit_id] === 'per_person';

    const pricingBehaviorOptions = [
        { id: 'program_once', name: isArabic ? 'مرة واحدة في البرنامج' : 'Program Once' },
        { id: 'per_occurrence', name: isArabic ? 'لكل تكرار' : 'Per Occurrence' }
    ];

    return (
        <Grid container spacing={3}>
            {/* Pricing Unit */}
            <Grid item xs={12} md={6}>
                <FormInput
                    label={isArabic ? 'وحدة التسعير' : 'Pricing Unit'}
                    type="select"
                    name="pricing_unit_id"
                    value={formData.pricing_unit_id || ''}
                    onChange={handlePricingUnitChange}
                    required
                    disabled={loadingPricingUnits}
                    error={!!errors.pricing_unit_id}
                    helperText={errors.pricing_unit_id}
                    options={pricingUnitsOptions}
                />
            </Grid>

            {/* Pricing Behavior (conditional on per_person) */}
            {isPerPerson && (
                <Grid item xs={12} md={6}>
                    <FormInput
                        label={isArabic ? 'سلوك التسعير' : 'Pricing Behavior'}
                        type="select"
                        name="pricing_behavior"
                        value={formData.pricing_behavior || ''}
                        onChange={(e) =>
                            setFormData(prev => ({
                                ...prev,
                                pricing_behavior: e.target.value || null,
                            }))
                        }
                        required={false}
                        error={!!errors.pricing_behavior}
                        helperText={errors.pricing_behavior}
                        options={pricingBehaviorOptions}
                    />
                </Grid>
            )}

            {/* City */}
            <Grid item xs={12} md={6}>
                <FormInput
                    label={isArabic ? 'المدينة' : 'City'}
                    type="select"
                    name="city_id"
                    value={formData.city_id || ''}
                    onChange={(e) =>
                        setFormData(prev => ({
                            ...prev,
                            city_id: e.target.value || null,
                        }))
                    }
                    required={false}
                    disabled={loadingCities}
                    error={!!errors.city_id}
                    helperText={errors.city_id}
                    options={cityOptions}
                />
            </Grid>

            {/* Rate Availability Start Date */}
            <Grid item xs={12} md={6}>
                {/* <DateInput
                    label={isArabic ? 'الوقت المتوفر يبدأ من' : 'Rate Availability Start Date'}
                    value={formData.rate_availability_start_date || ''}
                    onChange={(e) =>
                        setFormData({
                            ...formData,
                            rate_availability_start_date: e.target.value
                        })
                    }
                    required={false}
                    disablePast={true}
                    error={!!errors.rate_availability_start_date}
                    helperText={errors.rate_availability_start_date}
                /> */}
                <FormInput
                    label={isArabic ? 'الوقت المتوفر يبدأ من' : 'Rate Availability Start Date'}
                    type="date"
                    name="req_date"
                    value={formData.rate_availability_start_date || ''}
                    onChange={(e) =>
                        setFormData(prev => ({
                            ...prev,
                            rate_availability_start_date: e.target.value
                        }))
                    }
                    disablePast={true}
                    error={!!errors.rate_availability_start_date}
                    helperText={errors.rate_availability_start_date}
                />
            </Grid>

            {/* Rate Availability End Date */}
            <Grid item xs={12} md={6}>
                {/* <DateInput
                    label={isArabic ? 'الوقت المتوفر ينتهي ف' : 'Rate Availability End Date'}
                    value={formData.rate_availability_end_date || ''}
                    onChange={(e) =>
                        setFormData({
                            ...formData,
                            rate_availability_end_date: e.target.value
                        })
                    }
                    required={false}
                    disablePast={true}
                    min={formData.rate_availability_start_date
                        ? (() => {
                            const startDate = new Date(formData.rate_availability_start_date);
                            startDate.setDate(startDate.getDate() + 1);
                            return startDate.toISOString().split('T')[0];
                        })()
                        : ''
                    }
                    error={!!errors.rate_availability_end_date}
                    helperText={errors.rate_availability_end_date}
                /> */}
                <FormInput
                    type="date"
                    label={isArabic ? 'الوقت المتوفر ينتهي ف' : 'Rate Availability End Date'}
                    value={formData.rate_availability_end_date || ''}
                    onChange={(e) =>
                        setFormData(prev => ({
                            ...prev,
                            rate_availability_end_date: e.target.value
                        }))
                    }
                    required={false}
                    disablePast={true}
                    min={formData.rate_availability_start_date
                        ? (() => {
                            const startDate = new Date(formData.rate_availability_start_date);
                            startDate.setDate(startDate.getDate() + 1);
                            return startDate.toISOString().split('T')[0];
                        })()
                        : ''
                    }
                    error={!!errors.rate_availability_end_date}
                    helperText={errors.rate_availability_end_date}
                />
            </Grid>

            {/* Operating Days */}
            {showArabic && (
                <Grid item xs={12} md={6}>
                    <FormInput
                        label="أيام التشغيل (عربي)"
                        type="text"
                        name="operating_days_ar"
                        value={formData.operating_days?.ar || ''}
                        onChange={(e) =>
                            handleMultiLangChange('operating_days', 'ar', e.target.value)
                        }
                        placeholder="مثال: السبت - الخميس"
                        required={false}
                        error={!!errors.operating_days_ar}
                        helperText={errors.operating_days_ar}
                    />
                </Grid>
            )}

            {showEnglish && (
                <Grid item xs={12} md={6}>
                    <FormInput
                        label="Operating Days (English)"
                        type="text"
                        name="operating_days_en"
                        value={formData.operating_days?.en || ''}
                        onChange={(e) =>
                            handleMultiLangChange('operating_days', 'en', e.target.value)
                        }
                        placeholder="Example: Saturday - Thursday"
                        required={false}
                        error={!!errors.operating_days_en}
                        helperText={errors.operating_days_en}
                    />
                </Grid>
            )}

            {/* Operating Type */}
            {showArabic && (
                <Grid item xs={12} md={6}>
                    <FormInput
                        label="نوع التشغيل (عربي)"
                        type="text"
                        name="operating_type_ar"
                        value={formData.operating_type?.ar || ''}
                        onChange={(e) =>
                            handleMultiLangChange('operating_type', 'ar', e.target.value)
                        }
                        placeholder="مثال: يومي"
                        required={false}
                        error={!!errors.operating_type_ar}
                        helperText={errors.operating_type_ar}
                    />
                </Grid>
            )}

            {showEnglish && (
                <Grid item xs={12} md={6}>
                    <FormInput
                        label="Operating Type (English)"
                        type="text"
                        name="operating_type_en"
                        value={formData.operating_type?.en || ''}
                        onChange={(e) =>
                            handleMultiLangChange('operating_type', 'en', e.target.value)
                        }
                        placeholder="Example: Daily"
                        required={false}
                        error={!!errors.operating_type_en}
                        helperText={errors.operating_type_en}
                    />
                </Grid>
            )}
        </Grid>
    );
};

export default PricingOperationsSection;
